import { useEffect, useMemo } from 'react';
import { useGetEquityPrintFlowFullLazyQuery } from 'types/generated/client';
import { FINRA_EXCHANGE_ID } from 'constants/darkpool';
import getSignaturePrintStatus from 'utils/shared/trades/getSignaturePrintStatus';
import { getIsGoldenDarkPool } from 'utils/shared/trades/getIsGoldenDarkPool';
import { SignaturePrintStatus, EquityPrint } from 'utils/shared/trades/types';
import { getTickerFlowSinceDate } from 'utils/shared/trades/tickerFlowWindow';

export const useDarkPoolData = (ticker: string | undefined, isOpen: boolean) => {
  // PERF: literal rolling date bound so equity_print_flow_full uses the date index. See
  // utils/shared/trades/tickerFlowWindow.
  const sinceDate = useMemo(() => getTickerFlowSinceDate(), []);
  const [queryForTicker, { data: fullFeedData, loading, error }] =
    useGetEquityPrintFlowFullLazyQuery({
      variables: {
        ticker: ticker?.toUpperCase() || '',
        sinceDate,
      },
    });

  useEffect(() => {
    if (isOpen && ticker) {
      queryForTicker({
        variables: {
          ticker: ticker.toUpperCase(),
          sinceDate,
        },
      });
    }
  }, [isOpen, ticker, sinceDate, queryForTicker]);

  const darkPoolTrades = useMemo(() => {
    const allTrades = fullFeedData?.equity_print_flow_full || [];
    return allTrades.filter((t) => t.exchangeId === FINRA_EXCHANGE_ID);
  }, [fullFeedData]);

  const trades = useMemo(() => {
    // Return only the top 10 most recent trades
    return darkPoolTrades.slice(0, 10);
  }, [darkPoolTrades]);

  const darkPoolByTicker = useMemo(() => {
    const aggregatedData = {
      ticker: ticker?.toUpperCase() || '',
      trades: darkPoolTrades as any[],
      premium: 0,
      volume: 0,
      lateBuyCount: 0,
      lateSellCount: 0,
      goldenTradeCount: 0,
    };

    const open = darkPoolTrades.length > 0 && darkPoolTrades[0]?.open ? darkPoolTrades[0].open : 0;

    darkPoolTrades.forEach((t) => {
      const signaturePrintStatus = getSignaturePrintStatus(t as unknown as EquityPrint, { open });
      aggregatedData.premium += t.amount || 0;
      aggregatedData.volume += t.size || 0;

      if (signaturePrintStatus === SignaturePrintStatus.Buy) {
        aggregatedData.lateBuyCount += 1;
      } else if (signaturePrintStatus === SignaturePrintStatus.Sell) {
        aggregatedData.lateSellCount += 1;
      }

      if (getIsGoldenDarkPool(t.size, t.avgVolume)) {
        aggregatedData.goldenTradeCount += 1;
      }
    });

    return aggregatedData;
  }, [darkPoolTrades, ticker]);

  return {
    trades,
    darkPoolByTicker,
    loading,
    error,
  };
};
